/**
 * Port of `jsEntityEditObj.ValidateLead`, `ValidateEnquiry` and
 * `funcToCheckPrefixZero` from EntityEditDetails.js. Rules and messages are kept
 * as-is; only the shape changes — errors come back as a list rather than one
 * newline-joined string, so they can be rendered per field.
 */
import type { CustomFieldDef } from 'types/entity'

const EMAIL_RE = /^[\w-.+]+@[a-zA-Z0-9.-]+.[a-zA-Z]{2,4}$/
/** A dial code, one space, then 5-15 digits — e.g. "+91 9990998798". */
const MOBILE_RE = /^(\+[0-9]{1,5})\s([0-9]{5,15})$/

export interface ValidationInput {
  personName: string
  mobileNo: string
  mobileNo1: string
  mobileNo2: string
  emailId: string
  emailId1: string
  emailId2: string
  source: string
  pinCode: string
}

const ORDINALS = ['First', 'Second', 'Third'] as const

/** Indian numbers must be exactly 10 digits, and no number may start with 0. */
function validateMobile(mobile: string, position: 0 | 1 | 2): string[] {
  if (!mobile || !MOBILE_RE.test(mobile)) return []

  const errors: string[] = []
  const parts = mobile.split(' ')
  if (parts.length > 1) {
    const [code, number] = [parts[0].trim(), parts[1].trim()]
    if (code === '+91' && number.length !== 10) {
      errors.push(`Please Enter 10 digit Valid ${ORDINALS[position]} Mobile No. with Country Code +91`)
    }
    if (number.charAt(0) === '0') {
      errors.push(`Please remove 0 prefix from ${ORDINALS[position].toLowerCase()} mobile no`)
    }
  }
  return errors
}

function validateContactBlock(input: ValidationInput): string[] {
  const errors: string[] = []
  const { mobileNo, mobileNo1, mobileNo2, emailId, emailId1, emailId2 } = input

  const allEmpty = !mobileNo && !mobileNo1 && !mobileNo2 && !emailId && !emailId1 && !emailId2
  if (allEmpty) {
    errors.push('Please enter atleast one Mobile no.(with Country Code) or Email-ID')
    return errors
  }

  const mobiles = [mobileNo, mobileNo1, mobileNo2]
  if (mobiles.some((m) => m !== '' && !MOBILE_RE.test(m))) {
    errors.push('Please Enter Valid Mobile No. with Country Code')
  }
  mobiles.forEach((m, i) => errors.push(...validateMobile(m, i as 0 | 1 | 2)))

  const emails = [emailId, emailId1, emailId2]
  if (emails.some((e) => e !== '' && !EMAIL_RE.test(e))) {
    errors.push('Please Enter Valid Email-ID')
  }

  return errors
}

export function validateLead(input: ValidationInput): string[] {
  const errors: string[] = []

  if (!input.personName.trim()) errors.push('Please enter Person Name')
  errors.push(...validateContactBlock(input))

  const source = input.source?.trim()
  if (!source || source === '0') errors.push('Please select Source')

  if (input.pinCode !== '' && !isNumeric(input.pinCode)) errors.push('Please enter Valid Pincode')

  return errors
}

export function validateEnquiry(input: ValidationInput): string[] {
  const errors: string[] = []

  if (!input.personName.trim()) errors.push('Please enter Person Name')
  errors.push(...validateContactBlock(input))

  if (!input.source?.trim()) errors.push('Please select Source')

  if (input.pinCode !== '' && !isNumeric(input.pinCode)) errors.push('Please enter Valid Pincode')

  return errors
}

/** Mirrors `SaveLeadDetails`'s per-datatype custom-field checks. */
export function validateCustomFields(
  defs: CustomFieldDef[],
  values: Record<number, string | string[] | boolean>,
): string[] {
  const errors: string[] = []
  let mandatoryMissing = false

  for (const def of defs) {
    const raw = values[def.field_ID]
    const asString = Array.isArray(raw) ? raw.join(',') : typeof raw === 'boolean' ? String(raw) : (raw ?? '')
    const isEmpty = def.datatype === 'Checkbox' ? false : asString.trim() === ''

    if (def.isMandatory && isEmpty) {
      mandatoryMissing = true
      continue
    }
    if (isEmpty) continue

    switch (def.datatype) {
      case 'Numeric': {
        if (!isNumeric(asString)) {
          errors.push(`${def.fieldname}: Please enter Numeric value`)
        } else if (isNumeric(def.minvalue) && isNumeric(def.maxvalue)) {
          const value = Number(asString)
          if (value < Number(def.minvalue) || value > Number(def.maxvalue)) {
            errors.push(`${def.fieldname}: value must be between ${def.minvalue} and ${def.maxvalue}`)
          }
        }
        break
      }
      case 'Date': {
        const value = new Date(asString)
        const min = new Date(def.minvalue)
        const max = new Date(def.maxvalue)
        if (!Number.isNaN(value.getTime()) && !Number.isNaN(min.getTime()) && !Number.isNaN(max.getTime())) {
          if (value < min || value > max) {
            errors.push(`${def.fieldname}: value must be between ${def.minvalue} and ${def.maxvalue}`)
          }
        }
        break
      }
      default:
        break
    }
  }

  if (mandatoryMissing) errors.unshift('Please fill all mandatory fields (*)')
  return errors
}

export function isNumeric(value: string): boolean {
  if (value == null || String(value).trim() === '') return false
  return !Number.isNaN(Number(value))
}
